import React from "react";
import { X, Cake, Briefcase, Gift } from "lucide-react";
import { format } from "date-fns";

interface AnniversaryItem {
  employeeId: string;
  name: string;
  birthday?: { date: string; age: number };
  workAnniversary?: { date: string; yearsOfService: number };
}

interface AnniversaryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  birthdays: AnniversaryItem[];
  anniversaries: AnniversaryItem[];
}

const AnniversaryDetailModal: React.FC<AnniversaryDetailModalProps> = ({
  isOpen,
  onClose,
  birthdays,
  anniversaries,
}) => {
  if (!isOpen) return null;

  const totalCount = birthdays.length + anniversaries.length;
  const modalTitle = `Upcoming Celebrations (${totalCount})`;

  // Utility function for conditional list rendering
  const renderList = (
    items: AnniversaryItem[],
    type: "birthday" | "anniversary"
  ) => {
    const isBirthday = type === "birthday";
    const accentColor = isBirthday ? "indigo" : "teal";
    const Icon = isBirthday ? Cake : Briefcase;
    const titleText = isBirthday ? "Birthdays" : "Work Anniversaries";
    const colorClass = isBirthday ? "text-indigo-600" : "text-teal-600";
    const bgClass = isBirthday
      ? "bg-indigo-50 hover:bg-indigo-100"
      : "bg-teal-50 hover:bg-teal-100";
    const borderClass = isBirthday ? "border-indigo-400" : "border-teal-400";

    return (
      <div>
        <h3
          className={`text-xl font-extrabold ${colorClass} mb-4 flex items-center border-b border-gray-200 pb-3`}
        >
          <Icon className="h-5 w-5 mr-3" /> {titleText} ({items.length})
        </h3>
        <div className="space-y-4">
          {items.length > 0 ? (
            items.map((item) => {
              const dateData = isBirthday
                ? item.birthday
                : item.workAnniversary;
              const subtitle = isBirthday
                ? `Turning ${item.birthday?.age || "N/A"}`
                : `${
                    item.workAnniversary?.yearsOfService || "N/A"
                  } Years of Service`;
              const date = dateData?.date;

              if (!date) return null;

              return (
                <div
                  key={item.employeeId}
                  className={`p-4 rounded-xl ${bgClass} border-l-4 ${borderClass} shadow-sm transition duration-300 ease-in-out cursor-default`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800 text-lg">
                        {item.name}
                      </p>
                      <p className={`text-sm ${colorClass} font-medium mt-0.5`}>
                        {subtitle}
                      </p>
                    </div>
                    <span className={`text-lg font-bold ${colorClass}`}>
                      {format(new Date(date), "dd MMM")}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 italic p-4 bg-gray-100 rounded-lg">
              No {titleText.toLowerCase()} scheduled this period.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex justify-center items-center p-4 z-50 transition-opacity duration-300">
      {/* Modal Container */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden transform scale-100 transition-transform duration-300">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-20">
          <h2 className="text-3xl font-extrabold text-gray-800 flex items-center tracking-tight">
            <Gift className="h-7 w-7 mr-3 text-purple-600" />
            <span className="leading-tight">{modalTitle}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 p-2 rounded-full transition-all duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content Area - Scrollable Section */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Birthdays Column */}
            <div className="lg:border-r lg:pr-8 border-gray-200">
              {renderList(birthdays, "birthday")}
            </div>

            {/* Work Anniversaries Column */}
            <div className="lg:pl-8">
              {renderList(anniversaries, "anniversary")}
            </div>
          </div>
        </div>

        {/* Footer (Optional, can be used for actions/disclaimers) */}
        <div className="p-4 border-t border-gray-200 text-sm text-gray-500 bg-white sticky bottom-0 z-10 text-center">
        </div>
      </div>
    </div>
  );
};

export default AnniversaryDetailModal;
